import * as z from 'zod'
import { isPolicyHolderOrProxy } from '~/lib/utils'
import {
    injuredPersonValidator,
    witnessValidator,
} from '~/lib/zod/multi-person-fields/validator'
import {
    ACCEPTED_FILE_TYPES,
    MAX_FILE_SIZE_BYTES,
    MAX_FILE_SIZE_MB,
} from '~/models/generalConstants'

export const aboutYouValidator = z
    .object({
        PolicyNumber: z.string(),
        PolicyholderFirstName: z.string(),
        PolicyholderLastName: z.string(),
        partyReportingIncident: z.string(),
        insuranceProvider: z.string(),

        ReporterFirstName: z.string(),
        ReporterLastName: z.string(),
        relationshipToHolder: z.string(),
        ReporterAddressType: z.string(),
        ReporterAddressLine1: z.string(),
        ReporterAddressLine2: z.string().optional(),
        ReporterCountry: z.string(),
        ReporterParish: z.string().optional(),
        ReporterDistrict: z.string().optional(),
        ReporterIdentificationType: z.string(),
        ReporterIdentificationNumber: z.string(),
        ReporterPhoneType: z.string(),
        reportingPhoneNumber: z.string(),
        ReporterEmailType: z.string(),
        reportingEmailAddress: z.string().email(),
        ReporterContactPreference: z.string(),
        WasVehicleInUse: z.string(),
        AreYouDriver: z.string().optional(),

        AreYouCustodian: z.string().optional(),
        CustodianFirstName: z.string().optional(),
        CustodianLastName: z.string().optional(),
        CustodianPhoneNumber: z.string().optional(),
        CustodianEmailAddress: z.string().email().optional(),
        CustodianContactPreference: z.string().optional(),
        CustodianIdentificationNumber: z.string().optional(),
        CustodianRelationshipToHolder: z.string().optional(),
        CustodianAddressLine1: z.string().optional(),
        CustodianAddressLine2: z.string().optional(),
        CustodianParish: z.string().optional(),
    })
    .superRefine((data, refinementContext) => {
        if (data.WasVehicleInUse === 'yes') {
            if (!data.AreYouDriver) {
                refinementContext.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Required',
                    path: ['AreYouDriver'],
                })
            }
        }

        if (data.WasVehicleInUse === 'no') {
            if (!data.AreYouCustodian) {
                refinementContext.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Required',
                    path: ['AreYouCustodian'],
                })
            }
        }

        if (
            (data.AreYouCustodian === 'no' || data.AreYouDriver === 'no') &&
            isPolicyHolderOrProxy(data.partyReportingIncident)
        ) {
            if (!data.CustodianFirstName) {
                refinementContext.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Required',
                    path: ['CustodianFirstName'],
                })
            }

            if (!data.CustodianLastName) {
                refinementContext.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Required',
                    path: ['CustodianLastName'],
                })
            }
            if (!data.CustodianEmailAddress) {
                refinementContext.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Required',
                    path: ['CustodianEmailAddress'],
                })
            }
            if (!data.CustodianPhoneNumber) {
                refinementContext.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Required',
                    path: ['CustodianPhoneNumber'],
                })
            }
            if (!data.CustodianIdentificationNumber) {
                refinementContext.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Required',
                    path: ['CustodianIdentificationNumber'],
                })
            }
            if (!data.CustodianContactPreference) {
                refinementContext.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Required',
                    path: ['CustodianContactPreference'],
                })
            }
            if (!data.CustodianAddressLine1) {
                refinementContext.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Required',
                    path: ['CustodianAddressLine1'],
                })
            }
            if (!data.CustodianParish) {
                refinementContext.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Required',
                    path: ['CustodianParish'],
                })
            }
            if (!data.CustodianRelationshipToHolder) {
                refinementContext.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Required',
                    path: ['CustodianRelationshipToHolder'],
                })
            }
        }

        if (
            (data.ReporterCountry === 'barbados' ||
                data.ReporterCountry === 'jamaica') &&
            !data.ReporterParish
        ) {
            return refinementContext.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Required',
                path: ['ReporterParish'],
            })
        }
    })

export const aboutDriverValidator = z
    .object({
        AreYouPolicyHolder: z.string().optional(),
        AreYouDriver: z.string().optional(),
        DriverFirstName: z.string(),
        DriverLastName: z.string(),
        relationshipToHolder: z.string(),
        DriverPhoneType: z.string(),
        DriverPhoneNumber: z.string(),
        DriverEmailType: z.string(),
        DriverEmailAddress: z.string().email(),
        DriverLicenseType: z.string(),
        LicenseNumber: z.string(),
        gaveDriverConsent: z.string().optional(),
    })
    .superRefine((data, refinementContext) => {
        if (data.AreYouDriver === 'no' && !data.gaveDriverConsent) {
            return refinementContext.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Required',
                path: ['gaveDriverConsent'],
            })
        }
    })

export const whatHappenedValidator = z
    .object({
        DateOfEvent: z.date().refine((date) => date <= new Date(), {
            message: 'Date cannot be in the future',
        }),
        TimeOfAccident: z.string(),
        AddressLine1: z.string(),
        AddressLine2: z.string().optional(),
        Parish: z.string(),
        numberOfOccupants: z.number(),
        IsExternalFactorCause: z.string(),
        explanation: z.string().optional(),
        SignOfMechanicalIssues: z.string(),
        IsChargeConnected: z.string(),
        SignExplanation: z.string().optional(),

        activityAtTimeOfAccident: z.string().optional(),

        DetailsOfAccident: z.string(),
    })
    .superRefine(
        (
            {
                IsExternalFactorCause,
                explanation,
                SignOfMechanicalIssues,
                SignExplanation,
            },
            refinementContext
        ) => {
            if (IsExternalFactorCause === 'yes') {
                if (!explanation) {
                    refinementContext.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['explanation'],
                    })
                }
            }

            if (SignOfMechanicalIssues === 'yes') {
                if (!SignExplanation) {
                    refinementContext.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['SignExplanation'],
                    })
                }
            }
        }
    )

export const aboutVehicleValidator = z
    .object({
        VehicleYear: z.number(),
        VehicleMake: z.string(),
        VehicleModel: z.string(),
        VehicleRegistrationNumber: z.string(),
        VehicleChassisNumber: z.string(),
        VehicleEngineNumber: z.string().optional(),
        VehicleMortgagee: z.string(),
        nameFinanceCompany: z.string().optional(),
        branchFinanceCompany: z.string().optional(),
        bestContactPerson: z.string().optional(),
        phoneNumber: z.string().optional(),
        IsVehicleModified: z.string().optional(),
        ModifiedDetails: z.string().optional(),
        SpecialMarks: z.string().optional(),
    })
    .superRefine(
        (
            { VehicleMortgagee, nameFinanceCompany, branchFinanceCompany },
            refinementContext
        ) => {
            if (VehicleMortgagee === 'yes') {
                if (!nameFinanceCompany) {
                    refinementContext.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['nameFinanceCompany'],
                    })
                }

                if (!branchFinanceCompany) {
                    refinementContext.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['branchFinanceCompany'],
                    })
                }
            }
        }
    )

export const damageDetailsValidator = z
    .object({
        VehicleMortgagee: z.string(),
        isVehicleDamaged: z.string(),
        OwnerDamageDetails: z
            .array(z.string())
            .min(
                1,
                'Since your vehicle is damaged you have to select at least one area of damage.'
            )
            .optional(),
        isClaimForDamage: z.string().optional(),
        settlementOption: z.string().optional(),
        preferredRepairer: z.string().optional(),
        accountHolderName: z.string().optional(),
        bankAccountType: z.string().optional(),
        bankName: z.string().optional(),
        bankBranch: z.string().optional(),
        bankAccountNumber: z.string().optional(),
        routingNumber: z.string().optional(),
        isDeclarationApproved: z.string().optional(),

        additionalDamageDetails: z.string().optional(),
    })
    .superRefine(
        (
            {
                isVehicleDamaged,
                OwnerDamageDetails,
                isClaimForDamage,
                settlementOption,
                preferredRepairer,
                accountHolderName,
                bankAccountType,
                bankName,
                bankBranch,
                bankAccountNumber,
                isDeclarationApproved,
                VehicleMortgagee,
            },
            refinementContext
        ) => {
            if (isVehicleDamaged === 'yes') {
                if (!OwnerDamageDetails) {
                    refinementContext.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'You have to select at least one item.',
                        path: ['OwnerDamageDetails'],
                    })
                }

                if (!isClaimForDamage) {
                    refinementContext.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['isClaimForDamage'],
                    })
                }
            }

            if (isClaimForDamage === 'yes') {
                if (!settlementOption) {
                    refinementContext.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['settlementOption'],
                    })
                }

                if (settlementOption === 'repairs') {
                    if (!preferredRepairer) {
                        refinementContext.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Required',
                            path: ['preferredRepairer'],
                        })
                    }
                }

                if (settlementOption === 'cash') {
                    if (VehicleMortgagee === 'no') {
                        if (!accountHolderName) {
                            refinementContext.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: 'Required',
                                path: ['accountHolderName'],
                            })
                        }

                        if (!bankAccountType) {
                            refinementContext.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: 'Required',
                                path: ['bankAccountType'],
                            })
                        }

                        if (!bankName) {
                            refinementContext.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: 'Required',
                                path: ['bankName'],
                            })
                        }

                        if (!bankBranch) {
                            refinementContext.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: 'Required',
                                path: ['bankBranch'],
                            })
                        }

                        if (!bankAccountNumber) {
                            refinementContext.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: 'Required',
                                path: ['bankAccountNumber'],
                            })
                        }
                    }

                    if (!isDeclarationApproved) {
                        refinementContext.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Required',
                            path: ['isDeclarationApproved'],
                        })
                    }
                }
            }
        }
    )

export const otherDetailsValidator = z
    .object({
        WasVehicleInUse: z.string(),
        numberOfOccupants: z.number(),

        isAnyoneInjured: z.string().optional(),
        injuredPersons: z.array(injuredPersonValidator).optional(),

        isWitness: z.string(),
        witnesses: z.array(witnessValidator).optional(),

        isAccidentFootageAvailable: z.string().optional(),
        SupportingDocument: z
            .array(
                z
                    .instanceof(File)
                    .refine((file) => {
                        if (file?.type) {
                            return ACCEPTED_FILE_TYPES.includes(file.type)
                        }
                    }, 'File must be a pdf, jpeg, png or mp4')
                    .refine((file) => {
                        if (file?.size) {
                            return file.size <= MAX_FILE_SIZE_BYTES
                        }
                    }, `File must be ${MAX_FILE_SIZE_MB} MB or less in size`)
            )
            .optional(),
    })
    .superRefine(
        (
            { WasVehicleInUse, numberOfOccupants, isAnyoneInjured },
            refinementContext
        ) => {
            if (WasVehicleInUse === 'yes' && numberOfOccupants > 0) {
                if (!isAnyoneInjured) {
                    refinementContext.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['isAnyoneInjured'],
                    })
                }
            }
        }
    )
